import { useState, useCallback, useMemo } from "react";

interface SanityImageProps {
  src: string;
  alt: string;
  lqip?: string;
  width?: number;
  height?: number;
  loading?: "lazy" | "eager";
  className?: string;
  /** Extra classes applied only to the wrapper div */
  wrapperClassName?: string;
  /**
   * Responsive sizes hint (CSS sizes attribute).
   * Defaults to "100vw". Pass e.g. "(max-width:768px) 100vw, 50vw"
   * so the browser picks the smallest sufficient image.
   */
  sizes?: string;
}

/** Widths (px) we generate for the srcSet. Covers mobile → retina desktop. */
const SRCSET_WIDTHS = [320, 480, 640, 768, 1024, 1280, 1536, 1920];

/**
 * Append Sanity CDN params for a given width + auto format/compress.
 * Handles both urls that already have query params and those that don't.
 */
function sanityUrl(src: string, w: number): string {
  if (!src) return src;
  try {
    const url = new URL(src);
    // Remove params we will set ourselves so there are no duplicates
    url.searchParams.delete("w");
    url.searchParams.delete("h");
    url.searchParams.delete("fit");
    url.searchParams.delete("auto");
    url.searchParams.set("w", String(w));
    url.searchParams.set("auto", "format");
    url.searchParams.set("fit", "max");
    url.searchParams.set("q", url.searchParams.get("q") ?? "75");
    return url.toString();
  } catch {
    const sep = src.includes("?") ? "&" : "?";
    return `${src}${sep}w=${w}&auto=format&fit=max&q=75`;
  }
}

/**
 * Optimised image component for Sanity-sourced images.
 *
 * • Generates a responsive srcSet so browsers download only the size they need.
 * • Shows an LQIP (Low-Quality Image Placeholder) as a blurred background
 *   until the full image loads, eliminating CLS and giving an instant feel.
 * • Fades in the high-res image over 500ms once loaded.
 * • Falls back gracefully for non-Sanity URLs (no srcSet generated).
 */
const SanityImage = ({
  src,
  alt,
  lqip,
  width,
  height,
  loading = "lazy",
  className = "",
  wrapperClassName = "",
  sizes = "100vw",
}: SanityImageProps) => {
  const [loaded, setLoaded] = useState(false);

  const onLoad = useCallback(() => setLoaded(true), []);

  const isSanity = src?.includes("sanity.io") || src?.includes("cdn.sanity");

  const srcSet = useMemo(() => {
    if (!isSanity) return undefined;
    return SRCSET_WIDTHS.map((w) => `${sanityUrl(src, w)} ${w}w`).join(", ");
  }, [src, isSanity]);

  const optimisedSrc = isSanity ? sanityUrl(src, 1280) : src;

  return (
    <div
      className={`relative overflow-hidden ${wrapperClassName}`}
      style={
        lqip
          ? {
              backgroundImage: `url(${lqip})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : undefined
      }
    >
      <img
        src={optimisedSrc}
        srcSet={srcSet}
        sizes={isSanity ? sizes : undefined}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        onLoad={onLoad}
        className={`transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"} ${className}`}
      />
    </div>
  );
};

export default SanityImage;
